import { useAuth } from '@agentnet/auth';
import { Box, FormControlLabel, Grid, MenuItem, Switch, Typography } from '@material-ui/core';
import { grey, lightGreen, purple } from '@material-ui/core/colors';
import { createStyles, makeStyles, Theme } from '@material-ui/core/styles';
import {
  ColDef,
  GridApi,
  GridReadyEvent,
  ICellRendererParams,
  SelectionChangedEvent,
  ValueFormatterParams,
} from 'ag-grid-enterprise';
import { AgGridReactProps } from 'ag-grid-react';
import { firmSearchApi } from 'api/orderManagement/order-management-api';
import { searchInventoryPoliciesApi, updateInventoryPolicyStatusApi } from 'api/jacket-api';
import { format as formatDate } from 'date-fns';
import InventoryDrawer from 'features/orderManagement/InventoryDrawer';
import {
  SearchInventoryPoliciesRequestType,
  SearchInventoryPoliciesResponseType,
  FirmListType,
  InventoryCreationContext,
} from 'features/orderManagement/types';
import useAsync from 'hooks/useAsync';
import { useNavPermissions } from 'hooks/useNavPermissions';
import useSessionStorage from 'hooks/useSessionStorage';
import { useEffect, useRef, useState } from 'react';
import { CellStatus, DataTable, pxToRem, Status } from '@fluentsms/agentnet-web-components';
import AgentNetButton from 'ui-kit/components/button/AgentNetButton';
import BottomAppBar from 'ui-kit/components/bottomAppBar/BottomAppBar';
import PageHeader from 'ui-kit/components/headers/PageHeader';
import LoadingSpinner from 'ui-kit/components/LoadingSpinner';
import { AgentNetConfirmationDialog } from 'ui-kit/components/modal/ConfirmationDialog';
import useGlobalMessages from 'ui-kit/components/notification/useGlobalMessages';
import ContentContainer from 'ui-kit/components/utility/ContentContainer';
import { AgentNetTextInput } from 'ui-kit/inputs';
import AgentNetDropdownSelector from 'ui-kit/inputs/AgentNetDropdownSelector';
import AutocompleteFirmSearch from 'ui-kit/inputs/AutocompleteFirmSearch/AutocompleteFirmSearch';
import {
  getColorsByPolicyStatus,
  canVoidSelectedPolicies,
  canUnvoidSelectedPolicies,
  canTransferSelectedPolicies,
  getPolicyType,
} from './utility';

const dataQaPrefix = 'policyNumberManagement';

const getStartOfDay = (date: Date | string) => {
  const newDate = new Date(date);
  newDate.setHours(0, 0, 0, 0);
  return newDate;
};

const dateFilterParams = {
  comparator: (filterLocalDateAtMidnight: Date, cellValue: string) => {
    if (!cellValue) return -1;
    const cellDate = getStartOfDay(cellValue);
    if (filterLocalDateAtMidnight.getTime() === cellDate.getTime()) {
      return 0;
    }
    if (cellDate.getTime() < filterLocalDateAtMidnight.getTime()) {
      return -1;
    }
    if (cellDate.getTime() > filterLocalDateAtMidnight.getTime()) {
      return 1;
    }
    return 0;
  },
  browserDatePicker: true,
  inRangeInclusive: true,
};

const policyStatusDisplayMap: Record<string, string> = {
  'Virtual Policy Open': 'Open',
  'Virtual Policy Assigned': 'Assigned',
};

const getPolicyStatusDisplay = (status: string): string => policyStatusDisplayMap[status] ?? status;

const EJACKET_OPEN_STATUS = '1';
const VIRTUAL_POLICY_OPEN_STATUS = '9403';

const voidAndRemittedStatusOptions = [
  { name: 'Void', value: '4', color: purple[600] },
  { name: 'Remitted', value: '2', color: grey[600] },
];

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    noRows: {
      alignItems: 'center',
      display: 'flex',
      flexDirection: 'column',
      height: '100%',
      minHeight: 220,
      justifyContent: 'center',
    },
    errorText: {
      color: theme.palette.error.main,
      fontSize: pxToRem(12),
      marginTop: 4,
    },
  }),
);

interface ValidationErrors {
  policyNoStart?: string;
  policyNoEnd?: string;
  formNo?: string;
  accountNo?: string;
  policyStatus?: string;
}

export interface AdvancedSearchTextFieldsProps {
  initialPolicyNoStart: string;
  initialPolicyNoEnd: string;
  initialFileNo: string;
  initialFormNo: string;
  initialAccountNo: string;
  initialPolicyStatus: string;
  firmList: Array<FirmListType>;
  firm: FirmListType | undefined;
  firmSearchInput: string;
  noMatchesLabel: string;
  validationErrors: ValidationErrors;
  onClearSearch: () => void;
  onSearch: (params: {
    policyNoStart: string;
    policyNoEnd: string;
    fileNo: string;
    firmId: number;
    formNo: string;
    accountNo: string;
    policyStatus: string;
  }) => void;
  onFirmSearchChange: (value: string) => void;
  onFirmSelect: (event: any) => void;
  isSearching: boolean;
}

const AdvancedSearchTextFields = ({
  initialPolicyNoStart,
  initialPolicyNoEnd,
  initialFileNo,
  initialFormNo,
  initialAccountNo,
  initialPolicyStatus,
  firmList,
  firm,
  firmSearchInput,
  noMatchesLabel,
  validationErrors,
  onClearSearch,
  onSearch,
  onFirmSearchChange,
  onFirmSelect,
  isSearching,
}: AdvancedSearchTextFieldsProps) => {
  const classes = useStyles();
  const [policyNoStart, setPolicyNoStart] = useState<string>(initialPolicyNoStart);
  const [policyNoEnd, setPolicyNoEnd] = useState<string>(initialPolicyNoEnd);
  const [fileNo, setFileNo] = useState<string>(initialFileNo);
  const [formNo, setFormNo] = useState<string>(initialFormNo);
  const [accountNo, setAccountNo] = useState<string>(initialAccountNo);
  const [policyStatus, setPolicyStatus] = useState<string>(initialPolicyStatus);
  const [firmId, setFirmId] = useState<number | undefined>(firm?.value);

  // Sync local state with parent props when they change (e.g., after inventory creation)
  useEffect(() => {
    setPolicyNoStart(initialPolicyNoStart);
  }, [initialPolicyNoStart]);

  useEffect(() => {
    setPolicyNoEnd(initialPolicyNoEnd);
  }, [initialPolicyNoEnd]);

  useEffect(() => {
    setFileNo(initialFileNo);
  }, [initialFileNo]);

  useEffect(() => {
    setFormNo(initialFormNo);
  }, [initialFormNo]);

  useEffect(() => {
    setAccountNo(initialAccountNo);
  }, [initialAccountNo]);

  useEffect(() => {
    setPolicyStatus(initialPolicyStatus);
  }, [initialPolicyStatus]);

  useEffect(() => {
    setFirmId(firm?.value);
  }, [firm?.value]);

  const openStatusValue = !policyNoEnd ? EJACKET_OPEN_STATUS : VIRTUAL_POLICY_OPEN_STATUS;
  const policyStatusOptions = [
    { name: 'Open', value: openStatusValue, color: lightGreen[600] },
    ...voidAndRemittedStatusOptions,
  ];

  if (!firmList?.length && firm) {
    firmList.push(firm);
  }
  //When an account number is populated, the policy status search field is set to ‘Open’ by default
  useEffect(() => {
    if (accountNo && accountNo.trim() !== '' && !policyStatus) {
      setPolicyStatus(EJACKET_OPEN_STATUS);
    }
  }, [accountNo]);

  return (
    <>
      <Grid item xs={6} sm={3}>
        <AgentNetTextInput
          fullWidth
          data-testid={`${dataQaPrefix}-SearchField-PolicyNoStart`}
          label="Policy No. - Start"
          value={policyNoStart}
          error={!!validationErrors.policyNoStart}
          onChange={(event) => {
            if (event.target.value !== policyNoStart) {
              setPolicyNoStart(event.target.value?.trim() ?? '');
            }
          }}
        />
        {validationErrors.policyNoStart && (
          <Typography className={classes.errorText}>{validationErrors.policyNoStart}</Typography>
        )}
      </Grid>
      <Grid item xs={6} sm={3}>
        <AgentNetTextInput
          fullWidth
          data-testid={`${dataQaPrefix}-SearchField-PolicyNoEnd`}
          label="Policy No. - End"
          value={policyNoEnd}
          error={!!validationErrors.policyNoEnd}
          onChange={(event) => {
            if (event.target.value !== policyNoEnd) {
              setPolicyNoEnd(event.target.value?.trim() ?? '');
            }
          }}
        />
        {validationErrors.policyNoEnd && (
          <Typography className={classes.errorText}>{validationErrors.policyNoEnd}</Typography>
        )}
      </Grid>
      <Grid item xs={6} sm={3}>
        <AgentNetTextInput
          fullWidth
          data-testid={`${dataQaPrefix}-SearchField-FileNo`}
          label="File No."
          value={fileNo}
          onChange={(event) => {
            if (event.target.value !== fileNo) {
              setFileNo(event.target.value?.trim() ?? '');
            }
          }}
        />
      </Grid>
      <Grid item xs={6} sm={3}>
        <AutocompleteFirmSearch
          label="Firm Name"
          name="firmSearchInput"
          data-testid={`${dataQaPrefix}-SearchField-FirmName`}
          hideSearchIcon
          value={firmSearchInput ? firmId : -1}
          onChange={(event) => {
            if (typeof event === 'number') {
              setFirmId(event);
              onFirmSelect(event);
            } else if (typeof event === 'string') {
              onFirmSelect(0);
              setFirmId(undefined);
            } else if (typeof event === 'object') {
              onFirmSearchChange((event.target as HTMLInputElement)?.value);
            }
          }}
          options={firmList}
          noMatchesLabel={noMatchesLabel}
        />
      </Grid>

      <Grid item xs={6} sm={3}>
        <AgentNetTextInput
          fullWidth
          data-testid={`${dataQaPrefix}-SearchField-FormNo`}
          label="Form No."
          value={formNo}
          error={!!validationErrors.formNo}
          onChange={(event) => {
            if (event.target.value !== formNo) {
              setFormNo(event.target.value?.trim() ?? '');
            }
          }}
        />
        {validationErrors.formNo && <Typography className={classes.errorText}>{validationErrors.formNo}</Typography>}
      </Grid>
      <Grid item xs={6} sm={3}>
        <AgentNetTextInput
          fullWidth
          data-testid={`${dataQaPrefix}-SearchField-AccountNo`}
          label="Account No."
          value={accountNo}
          error={!!validationErrors.accountNo}
          onChange={(event) => {
            if (event.target.value !== accountNo) {
              setAccountNo(event.target.value?.trim() ?? '');
            }
          }}
        />
        {validationErrors.accountNo && (
          <Typography className={classes.errorText}>{validationErrors.accountNo}</Typography>
        )}
      </Grid>
      <Grid item xs={6} sm={3}>
        <AgentNetDropdownSelector
          name="policyStatus"
          data-testid={`${dataQaPrefix}-SearchField-PolicyStatus`}
          options={policyStatusOptions}
          value={policyStatus}
          id="policyStatus"
          dropdowntype="outlined"
          label="Policy Status"
          error={!!validationErrors.policyStatus}
          customMenuItems={policyStatusOptions.map((item) => (
            <MenuItem
              key={`${item.value}${item.name}`}
              value={item.value}
              style={{ display: 'flex', alignItems: 'center', gap: 8 }}
            >
              <Box
                style={{
                  width: 10,
                  height: 10,
                  borderRadius: '50%',
                  backgroundColor: item.color,
                }}
              />
              {item.name}
            </MenuItem>
          ))}
          menuOption={(value: string) => {
            setPolicyStatus(value);
            return false;
          }}
          renderValue={(value) => {
            const selectedOption = policyStatusOptions.find((opt) => opt.value === value);
            return selectedOption ? (
              <Box style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <Box
                  style={{
                    width: 10,
                    height: 10,
                    borderRadius: '50%',
                    backgroundColor: selectedOption.color,
                  }}
                />
                {selectedOption.name}
              </Box>
            ) : (
              ''
            );
          }}
        />
        {validationErrors.policyStatus && (
          <Typography className={classes.errorText}>{validationErrors.policyStatus}</Typography>
        )}
      </Grid>

      <Grid item xs={12}>
        <Grid container spacing={2}>
          <Grid item xs={3} sm={1}>
            <AgentNetButton
              color="primary"
              data-testid={`${dataQaPrefix}-Clear-Button`}
              onClick={() => {
                setPolicyNoStart('');
                setPolicyNoEnd('');
                setFileNo('');
                setFormNo('');
                setAccountNo('');
                setPolicyStatus('');
                onClearSearch();
              }}
              variant="outlined"
              size="small"
            >
              Clear All
            </AgentNetButton>
          </Grid>
          <Grid item xs={3} sm={1}>
            <AgentNetButton
              color="primary"
              data-testid={`${dataQaPrefix}-Search-Button`}
              variant="contained"
              onClick={() => {
                onSearch({
                  policyNoStart,
                  policyNoEnd,
                  fileNo,
                  firmId: firmId ?? 0,
                  formNo,
                  accountNo,
                  policyStatus,
                });
              }}
              size="small"
              disabled={isSearching}
            >
              {isSearching ? 'Searching...' : 'Search'}
            </AgentNetButton>
          </Grid>
        </Grid>
      </Grid>
    </>
  );
};

const PolicyNumberManagement = () => {
  const classes = useStyles();
  const { getAccessToken } = useAuth();
  const { addGlobalMsg } = useGlobalMessages();
  const { hasPermission } = useNavPermissions();

  const isViewOnly = hasPermission.inventoryManagementViewOnly;

  const [openDrawer, setOpenDrawer] = useState<boolean>(false);
  const [showSearchFields, setShowSearchFields] = useSessionStorage('policyNumberManagement_showSearchFields');
  const [searchResults, setSearchResults] = useState<SearchInventoryPoliciesResponseType[]>([]);
  const [validationErrors, setValidationErrors] = useState<ValidationErrors>({});

  // Selection and void/unvoid/transfer state
  const [selectedPolicies, setSelectedPolicies] = useState<SearchInventoryPoliciesResponseType[]>([]);
  const [showVoidModal, setShowVoidModal] = useState<boolean>(false);
  const [showUnvoidModal, setShowUnvoidModal] = useState<boolean>(false);
  const [showTransferModal, setShowTransferModal] = useState<boolean>(false);
  const [isProcessing, setIsProcessing] = useState<boolean>(false);
  const gridApiRef = useRef<GridApi | null>(null);

  const [firmList, setFirmList] = useState<Array<FirmListType>>([]);
  const [firmSearchInput, setFirmSearchInput] = useState<string>('');
  const [myFirm, setMyFirm] = useState<FirmListType | undefined>(undefined);
  const [noMatchesLabel, setNoMatchesLabel] = useState<string>('Enter at least 3 characters to refine search');
  const defaultNoMatchesMessage = 'Enter at least 3 characters to refine search';

  // The firm a transfer moves the selected inventory to, searched independently of the search panel's firm
  const [destinationFirmList, setDestinationFirmList] = useState<Array<FirmListType>>([]);
  const [destinationFirmSearchInput, setDestinationFirmSearchInput] = useState<string>('');
  const [destinationFirm, setDestinationFirm] = useState<FirmListType | undefined>(undefined);
  const [destinationNoMatchesLabel, setDestinationNoMatchesLabel] = useState<string>(
    'Enter at least 3 characters to refine search',
  );

  const [policyNoStart, setPolicyNoStart] = useState<string>('');
  const [policyNoEnd, setPolicyNoEnd] = useState<string>('');
  const [fileNo, setFileNo] = useState<string>('');
  const [formNo, setFormNo] = useState<string>('');
  const [accountNo, setAccountNo] = useState<string>('');
  const [policyStatus, setPolicyStatus] = useState<string>('');
  const [lastSearchPayload, setLastSearchPayload] = useState<SearchInventoryPoliciesRequestType | null>(null);
  const [searchFieldsKey, setSearchFieldsKey] = useState<number>(0);

  const colorsByPolicyStatus = getColorsByPolicyStatus();

  // setNoMatches is passed in so the search panel and the transfer dialog each keep their own label
  const firmSearch = async (input: string, setNoMatches: (label: string) => void): Promise<Array<FirmListType>> => {
    const token = await getAccessToken();
    const response = await firmSearchApi({ searchText: input }, token);
    const firmList: Array<FirmListType> = [];
    if (Array.isArray(response.result)) {
      response.result.forEach((firm) => {
        firmList.push({
          name: firm.firmName,
          value: firm.firmId,
          display: firm.firmName + ' (' + firm.firmId + ')',
          isActive: firm.isActive,
        });
      });
    }

    if (firmList.length === 1 && input.length > 5 && !isNaN(Number(input))) {
      firmList[0].accountNumber = input;
    } else if (firmList.length === 0 && input.length > 2 && !isNaN(Number(input))) {
      setNoMatches('Enter complete account number to refine search');
    } else {
      setNoMatches('No matching firms found');
    }
    return firmList;
  };

  useEffect(() => {
    const searchFirmsTimeout = setTimeout(async () => {
      if (firmSearchInput?.length > 2) {
        const result = await firmSearch(firmSearchInput, setNoMatchesLabel);
        setFirmList(result);
      } else {
        setNoMatchesLabel(defaultNoMatchesMessage);
      }
    }, 500);

    return () => clearTimeout(searchFirmsTimeout);
  }, [firmSearchInput]);

  useEffect(() => {
    const searchFirmsTimeout = setTimeout(async () => {
      if (destinationFirmSearchInput?.length > 2) {
        const result = await firmSearch(destinationFirmSearchInput, setDestinationNoMatchesLabel);
        setDestinationFirmList(result);
      } else {
        setDestinationNoMatchesLabel(defaultNoMatchesMessage);
      }
    }, 500);

    return () => clearTimeout(searchFirmsTimeout);
  }, [destinationFirmSearchInput]);

  const getSelectedFirmOption = (event: number) => {
    if (firmList.length > 0) {
      const selectedFirm = firmList.find((firm) => firm.value === event);
      if (selectedFirm) {
        setMyFirm(selectedFirm);
      }
    }
    if (!event) {
      setMyFirm(undefined);
    }
  };

  const clearFirmSearch = () => {
    setFirmList([]);
    setFirmSearchInput('');
    setMyFirm(undefined);
    setNoMatchesLabel(defaultNoMatchesMessage);
  };

  const getSelectedDestinationFirmOption = (event: number) => {
    if (destinationFirmList.length > 0) {
      const selectedFirm = destinationFirmList.find((firm) => firm.value === event);
      if (selectedFirm) {
        setDestinationFirm(selectedFirm);
      }
    }
    if (!event) {
      setDestinationFirm(undefined);
    }
  };

  const closeTransferModal = () => {
    setShowTransferModal(false);
    setDestinationFirmList([]);
    setDestinationFirmSearchInput('');
    setDestinationFirm(undefined);
    setDestinationNoMatchesLabel(defaultNoMatchesMessage);
  };

  const searchInventoryPolicies = async (
    payload: SearchInventoryPoliciesRequestType,
  ): Promise<SearchInventoryPoliciesResponseType[]> => {
    const token = await getAccessToken();
    return searchInventoryPoliciesApi(payload, token);
  };

  const {
    execute: executeSearchInventoryPolicies,
    status: searchStatus,
    value: searchValue,
    errors: searchErrors,
  } = useAsync<SearchInventoryPoliciesResponseType[]>(searchInventoryPolicies, false);

  const CustomOverlayLoading = () => {
    return (
      <div className={classes.noRows}>
        <LoadingSpinner status="pending" className="files-container--spinner-root" />
      </div>
    );
  };

  const handleClearSearch = () => {
    setPolicyNoStart('');
    setPolicyNoEnd('');
    setFileNo('');
    setFormNo('');
    setAccountNo('');
    setPolicyStatus('');
    clearFirmSearch();
    setSearchResults([]);
    setValidationErrors({});
  };

  const validateSearchFields = (params: {
    policyNoStart: string;
    policyNoEnd: string;
    fileNo: string;
    firmId: number;
    formNo: string;
    accountNo: string;
    policyStatus: string;
  }): boolean => {
    const errors: ValidationErrors = {};
    let isValid = true;

    // Rule 1: Account No. requires Policy Status
    if (params.accountNo && !params.policyStatus) {
      errors.accountNo = 'Account No. requires a Policy Status to be selected';
      isValid = false;
    }

    // Rule 2: Policy Status requires Account No. or Firm Name
    if (params.policyStatus && !params.accountNo && !params.firmId) {
      errors.policyStatus = 'Policy Status must be used with either Account No. or Firm Name';
      isValid = false;
    }

    setValidationErrors(errors);
    return isValid;
  };

  const handleSearch = (params: {
    policyNoStart: string;
    policyNoEnd: string;
    fileNo: string;
    firmId: number;
    formNo: string;
    accountNo: string;
    policyStatus: string;
  }) => {
    if (!validateSearchFields(params)) {
      return;
    }

    const payload: SearchInventoryPoliciesRequestType = {
      policyNoStart: params.policyNoStart || undefined,
      policyNoEnd: params.policyNoEnd || undefined,
      firmId: params.firmId || undefined,
      formNo: params.formNo || undefined,
      accountNo: params.accountNo ? Number(params.accountNo) : undefined,
      policyStatus: params.policyStatus ? Number(params.policyStatus) : undefined,
    };
    setLastSearchPayload(payload);
    executeSearchInventoryPolicies(payload);
  };

  const handleInventoryCreated = (context: InventoryCreationContext) => {
    // Show search fields if hidden
    if (!showSearchFields) {
      setShowSearchFields(true);
    }

    // Set firm in search criteria
    const createdFirm: FirmListType = {
      name: context.firmName,
      value: context.firmId,
      display: `${context.firmName} (${context.firmId})`,
      isActive: true,
      accountNumber: context.accountNumber,
    };
    setMyFirm(createdFirm);
    setFirmList([createdFirm]);
    setFirmSearchInput(createdFirm.display);

    // Set form number if available
    setFormNo(context.formNo ?? '');

    // Clear other fields for focused results
    setPolicyNoStart('');
    setPolicyNoEnd('');
    setFileNo('');
    setAccountNo('');
    setPolicyStatus('');
    setValidationErrors({});

    // Force AdvancedSearchTextFields to remount with new values
    setSearchFieldsKey((prev) => prev + 1);

    // Execute search by firm (and form if available)
    const payload: SearchInventoryPoliciesRequestType = {
      firmId: context.firmId,
      formNo: context.formNo || undefined,
    };
    setLastSearchPayload(payload);
    executeSearchInventoryPolicies(payload);
  };

  useEffect(() => {
    if (searchStatus === 'success') {
      // Deduplicate results by PolicyNumber to avoid showing duplicate rows
      const uniquePolicies = (searchValue ?? []).reduce<SearchInventoryPoliciesResponseType[]>((acc, policy) => {
        if (!acc.some((p) => p.PolicyNumber === policy.PolicyNumber)) {
          acc.push(policy);
        }
        return acc;
      }, []);
      setSearchResults(uniquePolicies);
    }
    if (searchStatus === 'error') {
      searchErrors?.forEach((err) => {
        addGlobalMsg({
          message: err,
          type: 'error',
        });
      });
    }
  }, [searchStatus]);

  const selectionColumnDef: ColDef = {
    headerName: '',
    field: 'selection',
    checkboxSelection: true,
    headerCheckboxSelection: true,
    headerCheckboxSelectionFilteredOnly: true,
    width: 50,
    minWidth: 50,
    maxWidth: 50,
    pinned: 'left',
    lockPosition: true,
    suppressMenu: true,
    filter: false,
    floatingFilter: false,
    sortable: false,
    resizable: false,
  };

  const columnDefs: ColDef[] = [
    // Selecting rows only exists to void or unvoid them, so view-only users do not get the column.
    ...(isViewOnly ? [] : [selectionColumnDef]),
    {
      headerName: 'Policy Status',
      cellRenderer: (params: ICellRendererParams) => {
        return (
          <CellStatus status={getPolicyStatusDisplay(params.value) as Status} colorsByStatus={colorsByPolicyStatus} />
        );
      },
      field: 'PolicyStatus',
      filter: 'agSetColumnFilter',
      minWidth: 130,
      maxWidth: 256,
      tooltipValueGetter: (params) => getPolicyStatusDisplay(params.value),
      suppressFloatingFilterButton: false,
    },
    {
      headerName: 'Policy No.',
      field: 'PolicyNumber',
      filter: 'agTextColumnFilter',
      minWidth: 140,
      maxWidth: 256,
      tooltipField: 'PolicyNumber',
    },
    {
      headerName: 'Form No.',
      field: 'FormNo',
      filter: 'agTextColumnFilter',
      minWidth: 120,
      maxWidth: 256,
      tooltipField: 'FormNo',
    },
    {
      headerName: 'Form Name',
      field: 'FormName',
      filter: 'agTextColumnFilter',
      minWidth: 150,
      maxWidth: 256,
      tooltipField: 'FormName',
    },
    {
      headerName: 'Form Valid In',
      field: 'StateCodes',
      filter: 'agTextColumnFilter',
      minWidth: 130,
      maxWidth: 256,
      valueFormatter: (params: ValueFormatterParams) => {
        return Array.isArray(params.value) ? params.value.join(', ') : '';
      },
      tooltipValueGetter: (params) => {
        return Array.isArray(params.value) ? params.value.join(', ') : '';
      },
    },
    {
      headerName: 'Account No.',
      field: 'AccountNo',
      filter: 'agNumberColumnFilter',
      minWidth: 130,
      maxWidth: 256,
      tooltipField: 'AccountNo',
    },
    {
      headerName: 'Account Name',
      field: 'AccountName',
      filter: 'agTextColumnFilter',
      minWidth: 150,
      maxWidth: 256,
      tooltipField: 'AccountName',
    },
    {
      headerName: 'File No.',
      field: 'FileNo',
      filter: 'agTextColumnFilter',
      minWidth: 120,
      maxWidth: 256,
      tooltipField: 'FileNo',
      valueFormatter: (params: ValueFormatterParams) => {
        return params?.value ?? '';
      },
      tooltipValueGetter: (params) => {
        return params?.value ?? '';
      },
    },
    {
      headerName: 'Created Date',
      field: 'CreatedDate',
      filter: 'agDateColumnFilter',
      filterParams: dateFilterParams,
      minWidth: 130,
      maxWidth: 256,
      valueFormatter: (params: ValueFormatterParams) => {
        return params?.value ? formatDate(new Date(params.value), 'MM/dd/yyyy') : '';
      },
      tooltipValueGetter: (params) => {
        return params?.value ? formatDate(new Date(params.value), 'MM/dd/yyyy') : '';
      },
    },
    {
      headerName: 'Last Modified Date',
      field: 'LastModifiedDate',
      filter: 'agDateColumnFilter',
      filterParams: dateFilterParams,
      minWidth: 130,
      maxWidth: 256,
      valueFormatter: (params: ValueFormatterParams) => {
        return params?.value ? formatDate(new Date(params.value), 'MM/dd/yyyy') : '';
      },
      tooltipValueGetter: (params) => {
        return params?.value ? formatDate(new Date(params.value), 'MM/dd/yyyy') : '';
      },
    },
  ];

  const handleSelectionChanged = (event: SelectionChangedEvent) => {
    const selected = event.api.getSelectedRows() as SearchInventoryPoliciesResponseType[];
    setSelectedPolicies(selected);
  };

  const agGridConfig = (): AgGridReactProps => {
    return {
      rowData: searchResults ?? [],
      columnDefs: columnDefs,
      defaultColDef: {
        editable: false,
        filter: 'agTextColumnFilter',
        flex: 1,
        floatingFilter: true,
        resizable: true,
        sortable: true,
        suppressFloatingFilterButton: true,
        suppressHeaderFilterButton: true,
        suppressHeaderMenuButton: true,
        suppressSizeToFit: false,
        width: 144,
        minWidth: 120,
      },
      rowSelection: isViewOnly ? undefined : 'multiple',
      suppressRowClickSelection: true,
      onSelectionChanged: isViewOnly ? undefined : handleSelectionChanged,
      onGridReady: (event: GridReadyEvent) => {
        gridApiRef.current = event.api;
        event.api.sizeColumnsToFit();
      },
    };
  };

  const handleVoidConfirm = async () => {
    setIsProcessing(true);
    try {
      const token = await getAccessToken();
      const payload = {
        data: selectedPolicies.map((policy) => ({
          policyNumber: policy.PolicyNumber,
          policyType: getPolicyType(policy),
        })),
        action: 'void' as const,
      };
      const response = await updateInventoryPolicyStatusApi(payload, token);

      if (response.successCount > 0) {
        addGlobalMsg({
          message: `Successfully voided ${response.successCount} ${
            response.successCount === 1 ? 'policy' : 'policies'
          }`,
          type: 'success',
        });
      }
      if (response.failureCount > 0) {
        response.results
          .filter((r) => !r.success)
          .forEach((r) => {
            addGlobalMsg({
              message: `Failed to void ${r.policyNumber}: ${r.errorMessage}`,
              type: 'error',
            });
          });
      }

      // Refresh the grid
      if (lastSearchPayload) {
        executeSearchInventoryPolicies(lastSearchPayload);
      }
      setSelectedPolicies([]);
      gridApiRef.current?.deselectAll();
    } catch (error) {
      addGlobalMsg({
        message: `Failed to void policies: ${error instanceof Error ? error.message : 'Unknown error'}`,
        type: 'error',
      });
    } finally {
      setIsProcessing(false);
      setShowVoidModal(false);
    }
  };

  const handleUnvoidConfirm = async () => {
    setIsProcessing(true);
    try {
      const token = await getAccessToken();
      const payload = {
        data: selectedPolicies.map((policy) => ({
          policyNumber: policy.PolicyNumber,
          policyType: getPolicyType(policy),
        })),
        action: 'unvoid' as const,
      };
      const response = await updateInventoryPolicyStatusApi(payload, token);

      if (response.successCount > 0) {
        addGlobalMsg({
          message: `Successfully unvoided ${response.successCount} ${
            response.successCount === 1 ? 'policy' : 'policies'
          }`,
          type: 'success',
        });
      }
      if (response.failureCount > 0) {
        response.results
          .filter((r) => !r.success)
          .forEach((r) => {
            addGlobalMsg({
              message: `Failed to unvoid ${r.policyNumber}: ${r.errorMessage}`,
              type: 'error',
            });
          });
      }

      // Refresh the grid
      if (lastSearchPayload) {
        executeSearchInventoryPolicies(lastSearchPayload);
      }
      setSelectedPolicies([]);
      gridApiRef.current?.deselectAll();
    } catch (error) {
      addGlobalMsg({
        message: `Failed to unvoid policies: ${error instanceof Error ? error.message : 'Unknown error'}`,
        type: 'error',
      });
    } finally {
      setIsProcessing(false);
      setShowUnvoidModal(false);
    }
  };

  const handleTransferConfirm = async () => {
    if (!destinationFirm) return;

    setIsProcessing(true);
    try {
      const token = await getAccessToken();
      const payload = {
        data: selectedPolicies.map((policy) => ({
          policyNumber: policy.PolicyNumber,
          policyType: getPolicyType(policy),
        })),
        action: 'transfer' as const,
        destinationFirmId: destinationFirm.value,
      };
      const response = await updateInventoryPolicyStatusApi(payload, token);

      if (response.successCount > 0) {
        addGlobalMsg({
          message: `Successfully transferred ${response.successCount} ${
            response.successCount === 1 ? 'policy' : 'policies'
          } to ${destinationFirm.display}`,
          type: 'success',
        });
      }
      if (response.failureCount > 0) {
        response.results
          .filter((r) => !r.success)
          .forEach((r) => {
            addGlobalMsg({
              message: `Failed to transfer ${r.policyNumber}: ${r.errorMessage}`,
              type: 'error',
            });
          });
      }

      // Refresh the grid. A transferred policy belongs to the destination firm with no account number,
      // so it drops out of a result set that was searched by the old firm or account.
      if (lastSearchPayload) {
        executeSearchInventoryPolicies(lastSearchPayload);
      }
      setSelectedPolicies([]);
      gridApiRef.current?.deselectAll();
    } catch (error) {
      addGlobalMsg({
        message: `Failed to transfer policies: ${error instanceof Error ? error.message : 'Unknown error'}`,
        type: 'error',
      });
    } finally {
      setIsProcessing(false);
      closeTransferModal();
    }
  };

  const renderPageHeaderContentRight = () => {
    return (
      <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
        <FormControlLabel
          control={
            <Switch
              name="SearchFields"
              checked={showSearchFields}
              onChange={() => {
                if (showSearchFields) {
                  handleClearSearch();
                }
                setShowSearchFields(!showSearchFields);
              }}
              data-testid={`${dataQaPrefix}-SearchFields-Toggle`}
            />
          }
          labelPlacement="start"
          label="Search Fields"
          style={{ marginRight: 8 }}
        />
        {!isViewOnly && (
          <AgentNetButton
            variant="contained"
            color="primary"
            size="small"
            plusIcon
            onClick={() => setOpenDrawer(true)}
            data-testid={`${dataQaPrefix}-AddInventory`}
          >
            Add Inventory
          </AgentNetButton>
        )}
      </div>
    );
  };

  const renderBottomAppBar = () => {
    if (isViewOnly || selectedPolicies.length === 0) return null;

    const selectedText = `Selecting ${selectedPolicies.length} ${
      selectedPolicies.length === 1 ? 'policy' : 'policies'
    }`;

    return (
      <BottomAppBar
        cancelLabel="Clear All"
        cancelDataQa={`${dataQaPrefix}-ClearSelection`}
        onCancel={() => {
          gridApiRef.current?.deselectAll();
          setSelectedPolicies([]);
        }}
        primaryActionLabel="Void"
        primaryActionDataQa={`${dataQaPrefix}-VoidPolicies`}
        disablePrimary={!canVoidSelectedPolicies(selectedPolicies)}
        disabledPrimaryHoverText="Selected policies cannot be voided"
        onPrimaryClick={() => setShowVoidModal(true)}
        secondaryActionLabel="Unvoid"
        secondaryActionDataQa={`${dataQaPrefix}-UnvoidPolicies`}
        disableSecondary={!canUnvoidSelectedPolicies(selectedPolicies)}
        onSecondaryClick={() => setShowUnvoidModal(true)}
        thirdActionLabel="Transfer"
        thirdActionDataQa={`${dataQaPrefix}-TransferPolicies`}
        disableThird={!canTransferSelectedPolicies(selectedPolicies)}
        onThirdClick={() => setShowTransferModal(true)}
        primaryLabel={selectedText}
      />
    );
  };

  if (searchStatus === 'pending' && !searchValue) {
    return (
      <ContentContainer fullWidth>
        <PageHeader divider={false} title="Policy Number Management" contentRight={renderPageHeaderContentRight()} />
        <CustomOverlayLoading />
      </ContentContainer>
    );
  }

  return (
    <ContentContainer fullWidth>
      <PageHeader divider={false} title="Policy Number Management" contentRight={renderPageHeaderContentRight()} />
      <Box p={3}>
        {showSearchFields && (
          <>
            <Grid container spacing={2}>
              <AdvancedSearchTextFields
                key={searchFieldsKey}
                initialPolicyNoStart={policyNoStart}
                initialPolicyNoEnd={policyNoEnd}
                initialFileNo={fileNo}
                initialFormNo={formNo}
                initialAccountNo={accountNo}
                initialPolicyStatus={policyStatus}
                firmList={firmList}
                firm={myFirm}
                firmSearchInput={firmSearchInput}
                noMatchesLabel={noMatchesLabel}
                validationErrors={validationErrors}
                onClearSearch={handleClearSearch}
                onSearch={(params) => {
                  setPolicyNoStart(params.policyNoStart);
                  setPolicyNoEnd(params.policyNoEnd);
                  setFileNo(params.fileNo);
                  setFormNo(params.formNo);
                  setAccountNo(params.accountNo);
                  setPolicyStatus(params.policyStatus);
                  handleSearch(params);
                }}
                onFirmSearchChange={(value) => setFirmSearchInput(value)}
                onFirmSelect={getSelectedFirmOption}
                isSearching={searchStatus === 'pending'}
              />
            </Grid>
          </>
        )}

        <Grid container spacing={2} style={{ marginTop: 16 }}>
          <Grid item xs={12} data-testid={`${dataQaPrefix}-Grid`}>
            <div style={{ height: selectedPolicies.length > 0 ? 540 : 600, width: '100%' }}>
              <DataTable {...agGridConfig()} fixedHeader fixedBottomScroll />
            </div>
            {searchResults && (
              <Typography variant="body2">
                Showing {searchResults.length} of {searchResults.length} policies
              </Typography>
            )}
          </Grid>
        </Grid>

        {renderBottomAppBar()}

        {openDrawer && (
          <InventoryDrawer
            openDrawer={openDrawer}
            setOpenDrawer={setOpenDrawer}
            onInventoryCreated={handleInventoryCreated}
          />
        )}

        <AgentNetConfirmationDialog
          open={showVoidModal}
          onDismissAction={() => setShowVoidModal(false)}
          onConfirm={handleVoidConfirm}
          dialogTitle="Void Policies"
          dialogText={`Are you sure you want to void ${selectedPolicies.length} ${
            selectedPolicies.length === 1 ? 'policy' : 'policies'
          }?`}
          dialogBtnContent="Void"
          disabled={isProcessing}
          qaAttrPrefix={`${dataQaPrefix}-VoidModal-`}
        />

        <AgentNetConfirmationDialog
          open={showUnvoidModal}
          onDismissAction={() => setShowUnvoidModal(false)}
          onConfirm={handleUnvoidConfirm}
          dialogTitle="Unvoid Policies"
          dialogText={`Are you sure you want to unvoid ${selectedPolicies.length} ${
            selectedPolicies.length === 1 ? 'policy' : 'policies'
          }?`}
          dialogBtnContent="Unvoid"
          disabled={isProcessing}
          qaAttrPrefix={`${dataQaPrefix}-UnvoidModal-`}
        />

        <AgentNetConfirmationDialog
          open={showTransferModal}
          onDismissAction={closeTransferModal}
          onConfirm={handleTransferConfirm}
          dialogTitle="Transfer Policies"
          dialogTextHTML={
            <>
              <Typography variant="body1" gutterBottom>
                {`Select the firm to transfer ${selectedPolicies.length} ${
                  selectedPolicies.length === 1 ? 'policy' : 'policies'
                } to. The policy status does not change, and the account number and office are cleared, so the transferred inventory arrives unassigned and will no longer appear in the current search results.`}
              </Typography>
              <AutocompleteFirmSearch
                label="Destination Firm"
                name="destinationFirmSearchInput"
                data-testid={`${dataQaPrefix}-TransferModal-DestinationFirm`}
                hideSearchIcon
                required
                value={destinationFirmSearchInput ? destinationFirm?.value : -1}
                onChange={(event) => {
                  if (typeof event === 'number') {
                    getSelectedDestinationFirmOption(event);
                  } else if (typeof event === 'string') {
                    setDestinationFirm(undefined);
                  } else if (typeof event === 'object') {
                    setDestinationFirmSearchInput((event.target as HTMLInputElement)?.value);
                  }
                }}
                options={destinationFirmList}
                noMatchesLabel={destinationNoMatchesLabel}
              />
            </>
          }
          dialogBtnContent="Transfer"
          disabled={isProcessing}
          disablePrimaryBtn={!destinationFirm || isProcessing}
          disableBackDropClick
          qaAttrPrefix={`${dataQaPrefix}-TransferModal-`}
        />
      </Box>
    </ContentContainer>
  );
};

export default PolicyNumberManagement;
